import React, { Suspense, lazy, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useScrollReset from "@hooks/ScrollHook";
import ActionDropdown from "@components/dropdown/ActionDropdown";
import useModal from "@hooks/ModalHook";
import { useApi } from "@hooks/ApiHook";
import { useAccount } from "@contexts/AccountContext";
import { ShareDropdown } from "@components/dropdown/ShareDropdown";
import TxSharesModal from "@components/modals/TxSharesModal";
import MintSharesModal from "@components/modals/MintSharesModal";
import { Tabs } from "@components/tabs/Tabs";
import ItemTab from "./tabs/ItemTab";
import MintableTab from "./tabs/MintableTab";
import NftTab from "./tabs/NftTab";
import BidsTab from "./tabs/BidsTab";

function CollectionPage() {
    useScrollReset()

    const { openModal } = useModal()
    const { address } = useAccount()

    const { query } = useParams();
    const navigate = useNavigate();

    const { data: collection, refresh: refreshCollection } = useApi(async api => api.collection.query(query!))
    const isCreator = () => address === collection?.ownerAddress

    return <>
        <main className="pt-[5.5rem] lg:pt-24">
            <div className="relative bg-default-image bg-gray-900">
                <img src={collection?.bannerUrl || "/src/img/logo/transparant_logo.png"}
                    alt="banner"
                    className="h-[18.75rem] object-cover mx-auto" />
            </div>

            <section className="relative bg-light-base pb-12 pt-28 dark:bg-dark">
                <div className="absolute left-1/2 top-0 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                    <figure className="relative">
                        <img
                            width={"150rem"}
                            src={collection?.thumbUrl || "/src/img/logo/transparant_logo.png"}
                            alt="collection avatar"
                            className="bg-default-image rounded-xl border-[5px] border-white dark:border-gray-400" />
                    </figure>
                </div>

                <div className="container flex flex-col items-center">
                    <div className="text-center mb-4">
                        <h2 className="mb-2 font-display text-4xl font-medium text-jacarta-700 dark:text-white">
                            {collection?.name || "Unnamed"}
                        </h2>
                        <div className="mb-4">
                            <span className="text-sm font-bold text-gray-400">Created by </span>
                            <Link to={`/user/${collection?.owner?.username}`}
                                className="text-sm font-bold text-jacarta-400">
                                {collection?.owner?.username}
                            </Link>
                        </div>

                        <p className="mx-auto max-w-xl text-lg dark:text-gray-400">
                            {collection?.description}
                        </p>


                        <div className="mt-4 flex items-center justify-center space-x-2.5">
                            {
                                isCreator() && <Link to={`/item/create?collectionId=${collection?.id}`}>
                                    <div className="rounded-xl border border-jacarta-100 bg-white hover:bg-jacarta-500 dark:hover:border-transparent dark:border-gray-400 dark:bg-gray-900 dark:hover:bg-jacarta-500">
                                        <div className="js-likes relative inline-flex h-10 w-10 cursor-pointer items-center justify-center text-sm before:absolute before:h-4 before:w-4 heart-fill-bg before:bg-cover before:bg-center before:bg-no-repeat before:opacity-0 fill-gray-400 hover:fill-white"
                                            role="button"
                                            data-tippy-content="Add Item">
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24">
                                                <path fill="none"
                                                    d="M0 0h24v24H0z" />
                                                <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
                                            </svg>
                                        </div>
                                    </div>
                                </Link>
                            }
                            {
                                (collection?.siteUrl || collection?.whitepaperUrl || collection?.discordUrl) && <ShareDropdown
                                    siteUrl={collection?.siteUrl}
                                    whitepaperUrl={collection?.whitepaperUrl}
                                    discordUrl={collection?.discordUrl}
                                />
                            }

                            {
                                isCreator() && <ActionDropdown
                                    items={
                                        [
                                            // {
                                            //     eventHandler: () => navigate(`/item/create/bulk?collectionId=${collection?.id}`),
                                            //     text: "Bulk Create",
                                            //     additionalClass: "text-green hover:text-white"
                                            // },
                                            {
                                                eventHandler: () => navigate(`/collection/${collection?.slug}/edit`),
                                                text: "Edit"
                                            },
                                            {
                                                eventHandler: () => openModal("tx-shares"),
                                                text: "Edit Tx Shares"
                                            },
                                            {
                                                eventHandler: () => openModal("mint-shares"),
                                                text: "Edit Mint Shares"
                                            },
                                            // {
                                            //     eventHandler: () => { },
                                            //     text: "Remove",
                                            //     additionalClass: "text-red"
                                            // },
                                        ]
                                    }
                                />
                            }

                        </div>
                    </div>
                </div>
            </section>

            {
                collection && <section className="relative py-24 pt-20">
                    <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
                        <img src="/src/img/gradient_light.jpg" alt="gradient" className="h-full w-full" />
                    </picture>

                    <div className="container">
                        <Tabs items={(() => {
                            const firstTabs: Record<string, JSX.Element> = {}
                            const lastTabs: Record<string, JSX.Element> = {}

                            if (isCreator()) {
                                firstTabs['Item'] = <ItemTab collection={collection} />
                            }

                            if (address) lastTabs['Bids'] = <BidsTab collection={collection} address={address!} />

                            return {
                                ...firstTabs,
                                "Mintable Item": <MintableTab collection={collection} />,
                                "NFT": <NftTab collection={collection} />,
                                ...lastTabs
                            }
                        })()} />
                    </div>

                </section>
            }

            {
                collection && <TxSharesModal
                    collection={collection}
                    onSaved={() => refreshCollection()} />
            }

            {
                collection && <MintSharesModal
                    collection={collection}
                    onSaved={() => refreshCollection()} />
            }
        </main>
    </>
}

export default CollectionPage;